import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import { TrendingUp, AlertCircle, Shield } from 'lucide-react';

export default function PlansPage() {
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { data: plans } = useQuery({ queryKey: ['plans'], queryFn: () => api.get('/investments/plans').then(r => r.data) });
  const investMutation = useMutation({
    mutationFn: (data: { planId: string; amount: number }) => api.post('/investments', data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['investments'] }); setSelectedPlan(null); setAmount(''); },
    onError: (err: any) => { setError(err.response?.data?.message || 'Investment failed'); },
  });

  const handleInvest = () => {
    if (!selectedPlan) return;
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { setError('Enter valid amount'); return; }
    if (amt < parseFloat(selectedPlan.minAmount) || amt > parseFloat(selectedPlan.maxAmount)) { setError('Amount must be between ' + formatCurrency(selectedPlan.minAmount) + ' and ' + formatCurrency(selectedPlan.maxAmount)); return; }
    setError('');
    investMutation.mutate({ planId: selectedPlan.id, amount: amt });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="d-page-header">
        <h1 className="text-2xl font-bold text-white">Investment Plans</h1>
        <p className="text-slate-400 mt-1">Choose a plan that suits your goals.</p>
      </div>

      <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
        <Shield className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-300">Risk Disclosure</p>
          <p className="text-xs text-amber-400/70 mt-0.5">Investment returns are not guaranteed. Past performance does not guarantee future results. Please invest responsibly.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans?.map((plan: any) => (
          <div key={plan.id} className="d-card-hover p-6 flex flex-col">
            <div className="mb-5">
              <h3 className="text-lg font-bold text-white">{plan.name}</h3>
              <p className="text-sm text-slate-400 mt-1">{plan.description}</p>
            </div>
            <div className="space-y-3 mb-6 flex-1">
              {[
                parseFloat(plan.minAmount) === parseFloat(plan.maxAmount)
                  ? { label: 'Price', value: formatCurrency(plan.minAmount) }
                  : { label: 'Investment Range', value: `${formatCurrency(plan.minAmount)} - ${formatCurrency(plan.maxAmount)}` },
                { label: 'Daily Rate', value: `${plan.dailyRate}%`, highlight: true },
                { label: 'Duration', value: `${plan.duration} days` },
                { label: 'Max Return', value: `${plan.maxEarningMultiple}x` },
              ].map((item) => (
                <div key={item.label} className="flex justify-between text-sm">
                  <span className="text-slate-400">{item.label}</span>
                  <span className={`font-medium ${item.highlight ? 'text-emerald-400' : 'text-white'}`}>{item.value}</span>
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={() => { setSelectedPlan(plan); setAmount(plan.minAmount === plan.maxAmount ? plan.minAmount : ''); setError(''); }}>
              Invest Now
            </Button>
          </div>
        ))}
      </div>

      <Modal isOpen={!!selectedPlan} onClose={() => setSelectedPlan(null)} title="Invest in Plan">
        {selectedPlan && (
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
                <AlertCircle className="w-4 h-4 shrink-0" />{error}
              </div>
            )}
            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <p className="font-medium text-white">{selectedPlan.name}</p>
              <p className="text-sm text-slate-400 mt-1">{selectedPlan.dailyRate}% daily for {selectedPlan.duration} days</p>
            </div>
            <Input
              label={parseFloat(selectedPlan.minAmount) === parseFloat(selectedPlan.maxAmount) ? `Amount (${formatCurrency(selectedPlan.minAmount)})` : `Amount (${formatCurrency(selectedPlan.minAmount)} - ${formatCurrency(selectedPlan.maxAmount)})`}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
            />
            <Button className="w-full" isLoading={investMutation.isPending} onClick={handleInvest}>
              Confirm Investment
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
